import React, { useRef, useContext, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import moment from "moment/moment";
import CurrencyFormat from "react-currency-format";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import { rewardService } from "../../services/Reward.service";
import LogicContext from "../../Context/LogicContext";
import RewardScroller from "./RewardScroller";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";

const RewardHistoryDetails = () => {
  const [selectedList, setSelectedList] = useState([]);
  const myPromiseModal = createModal(GeneralModal);
  const { id } = useParams();
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);
  const [historyDetails, setHistoryDetails] = useState({});
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const componentRef = useRef();

  useEffect(() => {
    document.title = "Reward history details";
    getHistoryDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getHistoryDetails = async (id) => {
    rewardService.rewardHistoryDetails(id).then((response) => {
      if (response.data[0]) {
        setHistoryDetails(response.data[0]);
      }
    });
  };

  function viewItemDetails(id) {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
    getHistoryDetails(id);
  }

  const editCurrentData = async () => {
    await myPromiseModal({
      title: "Modify Corporate Data",
      body: "Do you want to modify this record?",
      showInput: false,
      placeHolder: "",
    });
  };

  function onSelection(list) {
    let tempList = list?.filter((item) => item.checked === true);
    setSelectedList(tempList);
  }

  const manageStatus = (statusCode, statusName) => {
    let defaultClass = "bg-blue-500 text-white";
    switch (statusCode) {
      case 1:
        defaultClass = "bg-green-500 text-white";
        break;
      case 2:
        defaultClass = "bg-yellow-500 text-white";
        break;
      case 3:
        defaultClass = "bg-red-500 text-white";
        break;
      case 4:
        defaultClass = "bg-purple-500 text-white";
        break;
      case 5:
        defaultClass = "bg-indigo-500 text-white";
        break;
      case 6:
        defaultClass = "bg-gray-500 text-white";
        break;
      default:
        break;
    }
    return (
      <span className={`py-1 px-3 rounded-full text-sm font-medium ${defaultClass}`}>
        {statusName}
      </span>
    );
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Reward History Details",
    pageStyle: `
      @media print {
        .no-print {
          display: none !important;
        }
        .print-section {
          margin: 20px;
          box-shadow: none !important;
          border: none !important;
        }
        .print-section > div {
          margin-bottom: 16px;
          page-break-inside: avoid;
        }
      }
    `,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Segment 1: Header with Print Button */}
      <header className="bg-white shadow-md py-4 no-print">
        <div className="max-w-6xl mx-auto px-4 flex justify-end">
          <button
            onClick={handlePrint}
            className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Print
          </button>
        </div>
      </header>

      {/* Segment 2 & 3: Reward Scroller and Details Section */}
      <section className="max-w-6xl mx-auto px-4 py-6 flex flex-col md:flex-row gap-6">
        {/* Reward Scroller */}
        <div className="w-full md:w-3/12 bg-white rounded-lg shadow-md p-4 no-print">
          <RewardScroller onSelection={onSelection} viewItemDetails={viewItemDetails} />
        </div>

        {/* Details Section */}
        <div ref={componentRef} className="w-full md:w-9/12 space-y-6 print-section">
          {/* Corporate Info */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-blue-600 uppercase mb-4">
              {historyDetails?.Corporate_Name}
            </h2>
            <div className="space-y-2">
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Corporate ID:</span>
                <span className="text-gray-800">{historyDetails?.Corporate_Id}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Ref Number:</span>
                <span className="text-gray-800">{historyDetails?.Ref_No}</span>
              </div>
            </div>
          </div>

          {/* Cowries Info */}
          <div className="bg-white rounded-lg shadow-md p-6 flex items-center justify-between">
            <span className="text-lg font-bold text-blue-500">
              {historyDetails?.Reward_Cowries_Quantity} Cowrie(s)
            </span>
            {historyDetails?.Transaction_Type_Name && (
              <span>{manageStatus(historyDetails?.Transaction_Type_Id, historyDetails?.Transaction_Type_Name)}</span>
            )}
          </div>

          {/* Transaction Info */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-2">
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Transaction Date:</span>
                <span className="text-gray-800">
                  {moment(historyDetails?.Transaction_Date).format("dddd, MMMM Do YYYY, h:mm:ss a")}
                </span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40"> Transaction Ref:</span>
                <span className="text-gray-800">{historyDetails?.Transaction_Ref_No}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-2">
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Email Address:</span>
                <span className="text-gray-800">{historyDetails?.Customer_Email_Address}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Name:</span>
                <span className="text-gray-800">{historyDetails?.Customer_Full_Name}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Customer ID:</span>
                <span className="text-gray-800">{historyDetails?.Customer_Id}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Customer Mobile:</span>
                <span className="text-gray-800">{historyDetails?.Customer_Mobile_No}</span>
              </div>
            </div>
          </div>

          {/* Reward Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-2">
              <div className="flex items-left">
                <span className="font-semibold text-gray-600 w-40">Title:</span>
                <span className="text-gray-800 ">{historyDetails?.Reward_Earn_Title}</span>
              </div>
              <div className="flex items-left">
                <span className="font-semibold text-gray-600 w-40">Description:</span>
                <span className="text-gray-800">{historyDetails?.Reward_Earn_Description}</span>
              </div>
             
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Reward Cowries:</span>
                <span className="text-gray-800">{historyDetails?.Reward_Cowries_Quantity}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Remark:</span>
                <span className="text-gray-800">{historyDetails?.Reward_Remark}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Transaction Type:</span>
                <span className="text-gray-800">{historyDetails?.Transaction_Type_Name}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 w-40">Transaction Amount:</span>
                <span className="text-gray-800">
                  <CurrencyFormat
                    className="font-bold"
                    value={formatCustomNumber(historyDetails?.Reward_Amount)}
                    displayType="text"
                    thousandSeparator={true}
                    prefix="₦"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RewardHistoryDetails;